import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { US_STATES, addressUpdateSchema } from '@app/shared';
import type { AddressUpdateInput, CustomerDto } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { SelectInput, TextInput, applyServerErrors } from '../../components/form';
import { withRequiredMessages } from '../inventory/formUtils';
import { useChangeAddress } from './api';

type Scope = AddressUpdateInput['scope'];

interface AddressForm {
  scope: Scope;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

const SCOPES: Array<[Scope, string]> = [
  ['service', 'Service Address'],
  ['mailing', 'Mailing Address'],
  ['both', 'Both'],
];
const STATE_OPTIONS = US_STATES.map(([code, name]) => ({
  value: code,
  label: `${name} [${code}]`,
}));

/** The address the form starts from: the mailing address for "Mailing Address", else the service address. */
const startValues = (customer: CustomerDto, scope: Scope): AddressForm => {
  const { line1, line2, city, state, zip } =
    scope === 'mailing' ? customer.mailingAddress : customer.serviceAddress;
  return { scope, line1, line2, city, state, zip };
};

/**
 * Change Address (PC100): one form for the service address, the mailing address or both.
 * "Address1" is marked required although the reference does not mark it, because the API
 * does not accept an address without it.
 */
export function ChangeAddressForm({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const changeAddress = useChangeAddress(customer.id);
  const { control, handleSubmit, reset, setError } = useForm<AddressForm>({
    defaultValues: startValues(customer, 'service'),
    resolver: withRequiredMessages<AddressForm>(
      zodResolver(addressUpdateSchema) as unknown as Resolver<AddressForm>,
      { state: 'State is required', zip: 'Zip Code is required' },
    ),
  });

  const submit = handleSubmit(async (values) => {
    try {
      await changeAddress.mutateAsync(values as AddressUpdateInput);
      notify.success('Address updated.');
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <Box component="form" noValidate onSubmit={(e) => void submit(e)} aria-label="Change Address">
      <Controller
        control={control}
        name="scope"
        render={({ field }) => (
          <FormControl sx={{ mb: 1.5 }}>
            {/* The reference shows the three choices without a caption. */}
            <RadioGroup
              row
              aria-label="Address to change"
              name={field.name}
              value={field.value}
              // Each choice starts from the address it is about to replace.
              onChange={(_e, value) => reset(startValues(customer, value as Scope))}
            >
              {SCOPES.map(([value, label]) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  label={label}
                  control={<Radio size="small" />}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          label="First Name"
          value={customer.firstName}
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label="Last Name"
          value={customer.lastName}
          slotProps={{ input: { readOnly: true } }}
        />
        <TextInput
          control={control}
          name="zip"
          label="Zip Code"
          required
          slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
        />
        <TextInput
          control={control}
          name="city"
          label="City"
          required
          slotProps={{ htmlInput: { maxLength: 60 } }}
        />
        <SelectInput
          control={control}
          name="state"
          label="State"
          required
          options={STATE_OPTIONS}
        />
        <Box />
        <TextInput
          control={control}
          name="line1"
          label="Address1"
          required
          slotProps={{ htmlInput: { maxLength: 120 } }}
        />
        <TextInput
          control={control}
          name="line2"
          label="Address2"
          slotProps={{ htmlInput: { maxLength: 120 } }}
        />
      </Box>
      <Button type="submit" variant="contained" disabled={changeAddress.isPending} sx={{ mt: 2 }}>
        Change Address
      </Button>
    </Box>
  );
}
